/**
 * Cookie utility functions for secure token storage
 */

/**
 * Set a cookie with secure defaults
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} [options={}] - Cookie options
 */
export const setCookie = (name, value, options = {}) => {
  const defaults = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
    maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
  };

  const cookieOptions = { ...defaults, ...options };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Add path
  if (cookieOptions.path) {
    cookieString += `; path=${cookieOptions.path}`;
  }

  // Add maxAge (this is what makes the cookie persistent)
  if (cookieOptions.maxAge !== undefined && cookieOptions.maxAge !== null) {
    cookieString += `; max-age=${cookieOptions.maxAge}`;
  }

  // Add expires as backup for older browsers
  if (cookieOptions.maxAge && cookieOptions.maxAge > 0) {
    const expiresDate = new Date();
    expiresDate.setTime(expiresDate.getTime() + cookieOptions.maxAge * 1000);
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }

  // Add secure flag (important for HTTPS)
  if (cookieOptions.secure) {
    cookieString += '; secure';
  }

  // Add sameSite attribute (CRITICAL: proper format)
  if (cookieOptions.sameSite) {
    cookieString += `; samesite=${cookieOptions.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${encodeURIComponent(name)}=`);

  if (parts.length === 2) {
    const cookieValue = parts.pop().split(';').shift();
    return decodeURIComponent(cookieValue);
  }

  return null;
};

/**
 * Remove a cookie by setting it to expire
 * @param {string} name - Cookie name
 * @param {string} [path='/'] - Cookie path
 */
export const removeCookie = (name, path = '/') => {
  document.cookie = `${encodeURIComponent(
    name
  )}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; secure; sameSite=strict`;
};

/**
 * Get all cookies as an object
 * @returns {Object} Object with cookie name-value pairs
 */
export const getAllCookies = () => {
  const cookies = {};

  document.cookie.split(';').forEach((cookie) => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });

  return cookies;
};

/**
 * Clear all cookies
 */
export const clearCookies = () => {
  Object.entries(getAllCookies()).forEach(([name]) => {
    removeCookie(name);
  });
};
